/**
 * Chat Settings Constants
 *
 * These constants define the account_settings keys used for the AI Chat Agent feature.
 * Settings follow the pattern: chat_{setting_name}
 */

/**
 * All chat-related setting keys for account_settings table
 */
export const CHAT_SETTINGS = {
  // Core Settings
  /** Enable/disable chat widget (boolean: 'true' or 'false') */
  ENABLED: 'chat_enabled',

  /** Groq API key for LLM inference (string, is_secret: true) */
  GROQ_API_KEY: 'chat_groq_api_key',

  /** Initial greeting message shown when chat opens (string) */
  GREETING_MESSAGE: 'chat_greeting_message',

  /** Custom system prompt/personality instructions for the AI (string) */
  SYSTEM_PROMPT: 'chat_system_prompt',

  // Widget Appearance
  /** Widget position on screen ('bottom-right' or 'bottom-left') */
  WIDGET_POSITION: 'chat_widget_position',

  /** Widget color theme ('light', 'dark', or 'auto') */
  WIDGET_THEME: 'chat_widget_theme',

  /** Primary accent color for the widget (hex color string, e.g., '#3B82F6') */
  PRIMARY_COLOR: 'chat_primary_color',

  // Callback Settings
  /** Enable/disable callback request feature (boolean: 'true' or 'false') */
  CALLBACK_ENABLED: 'chat_callback_enabled',

  /** Email to notify when callback is requested (string) */
  CALLBACK_EMAIL: 'chat_callback_email',

  // Live Handoff Settings
  /** Enable/disable live agent handoff feature (boolean: 'true' or 'false') */
  LIVE_HANDOFF_ENABLED: 'chat_live_handoff_enabled',

  /** Email to notify when handoff is requested (string) */
  LIVE_HANDOFF_EMAIL: 'chat_live_handoff_email',

  /** Phone number to display for live handoff (string) */
  LIVE_HANDOFF_PHONE: 'chat_live_handoff_phone',

  // Proactive Engagement
  /** Delay in seconds before showing proactive message (number, 0 to disable) */
  PROACTIVE_TRIGGER_DELAY: 'chat_proactive_delay',

  /** Proactive engagement message text (string) */
  PROACTIVE_MESSAGE: 'chat_proactive_message',

  // Limits
  /** Maximum messages allowed per conversation (number) */
  MAX_MESSAGES_PER_CONVERSATION: 'chat_max_messages',
} as const

/**
 * Type for chat setting keys
 */
export type ChatSettingKey = (typeof CHAT_SETTINGS)[keyof typeof CHAT_SETTINGS]

/**
 * Widget position options
 */
export const CHAT_WIDGET_POSITIONS = ['bottom-right', 'bottom-left'] as const
export type ChatWidgetPosition = (typeof CHAT_WIDGET_POSITIONS)[number]

/**
 * Widget theme options
 */
export const CHAT_WIDGET_THEMES = ['light', 'dark', 'auto'] as const
export type ChatWidgetTheme = (typeof CHAT_WIDGET_THEMES)[number]

/**
 * Default values for chat settings
 */
export const CHAT_SETTINGS_DEFAULTS = {
  [CHAT_SETTINGS.ENABLED]: 'false',
  [CHAT_SETTINGS.GREETING_MESSAGE]:
    "Hi there! I'm here to help you book your next ride. How can I assist you today?",
  [CHAT_SETTINGS.SYSTEM_PROMPT]: '',
  [CHAT_SETTINGS.WIDGET_POSITION]: 'bottom-right' as ChatWidgetPosition,
  [CHAT_SETTINGS.WIDGET_THEME]: 'auto' as ChatWidgetTheme,
  [CHAT_SETTINGS.PRIMARY_COLOR]: '#3B82F6',
  [CHAT_SETTINGS.CALLBACK_ENABLED]: 'false',
  [CHAT_SETTINGS.CALLBACK_EMAIL]: '',
  [CHAT_SETTINGS.LIVE_HANDOFF_ENABLED]: 'false',
  [CHAT_SETTINGS.LIVE_HANDOFF_EMAIL]: '',
  [CHAT_SETTINGS.LIVE_HANDOFF_PHONE]: '',
  [CHAT_SETTINGS.PROACTIVE_TRIGGER_DELAY]: '0',
  [CHAT_SETTINGS.PROACTIVE_MESSAGE]:
    "Need help finding the perfect ride? I'm here to assist!",
  [CHAT_SETTINGS.MAX_MESSAGES_PER_CONVERSATION]: '50',
} as const

/**
 * Settings that should be marked as is_secret: true in account_settings
 */
export const CHAT_SECRET_SETTINGS: ChatSettingKey[] = [CHAT_SETTINGS.GROQ_API_KEY]

/**
 * All chat setting keys as an array (useful for fetching)
 */
export const ALL_CHAT_SETTING_KEYS = Object.values(CHAT_SETTINGS)

/**
 * Interface for parsed chat settings (with proper types)
 */
export interface ChatSettings {
  enabled: boolean
  groqApiKey: string | null
  greetingMessage: string
  systemPrompt: string
  widgetPosition: ChatWidgetPosition
  widgetTheme: ChatWidgetTheme
  primaryColor: string
  callbackEnabled: boolean
  callbackEmail: string
  liveHandoffEnabled: boolean
  liveHandoffEmail: string
  liveHandoffPhone: string
  proactiveDelay: number
  proactiveMessage: string
  maxMessagesPerConversation: number
}

/**
 * Parse raw setting values from account_settings into typed ChatSettings
 */
export function parseChatSettings(
  rawSettings: Record<string, string | null | undefined>
): ChatSettings {
  const get = (key: string, defaultValue: string): string =>
    rawSettings[key] ?? defaultValue

  return {
    enabled: get(CHAT_SETTINGS.ENABLED, CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.ENABLED]) === 'true',
    groqApiKey: rawSettings[CHAT_SETTINGS.GROQ_API_KEY] ?? null,
    greetingMessage: get(
      CHAT_SETTINGS.GREETING_MESSAGE,
      CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.GREETING_MESSAGE]
    ),
    systemPrompt: get(
      CHAT_SETTINGS.SYSTEM_PROMPT,
      CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.SYSTEM_PROMPT]
    ),
    widgetPosition: get(
      CHAT_SETTINGS.WIDGET_POSITION,
      CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.WIDGET_POSITION]
    ) as ChatWidgetPosition,
    widgetTheme: get(
      CHAT_SETTINGS.WIDGET_THEME,
      CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.WIDGET_THEME]
    ) as ChatWidgetTheme,
    primaryColor: get(
      CHAT_SETTINGS.PRIMARY_COLOR,
      CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.PRIMARY_COLOR]
    ),
    callbackEnabled:
      get(CHAT_SETTINGS.CALLBACK_ENABLED, CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.CALLBACK_ENABLED]) ===
      'true',
    callbackEmail: get(
      CHAT_SETTINGS.CALLBACK_EMAIL,
      CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.CALLBACK_EMAIL]
    ),
    liveHandoffEnabled:
      get(
        CHAT_SETTINGS.LIVE_HANDOFF_ENABLED,
        CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.LIVE_HANDOFF_ENABLED]
      ) === 'true',
    liveHandoffEmail: get(
      CHAT_SETTINGS.LIVE_HANDOFF_EMAIL,
      CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.LIVE_HANDOFF_EMAIL]
    ),
    liveHandoffPhone: get(
      CHAT_SETTINGS.LIVE_HANDOFF_PHONE,
      CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.LIVE_HANDOFF_PHONE]
    ),
    proactiveDelay: parseInt(
      get(
        CHAT_SETTINGS.PROACTIVE_TRIGGER_DELAY,
        CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.PROACTIVE_TRIGGER_DELAY]
      ),
      10
    ),
    proactiveMessage: get(
      CHAT_SETTINGS.PROACTIVE_MESSAGE,
      CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.PROACTIVE_MESSAGE]
    ),
    maxMessagesPerConversation: parseInt(
      get(
        CHAT_SETTINGS.MAX_MESSAGES_PER_CONVERSATION,
        CHAT_SETTINGS_DEFAULTS[CHAT_SETTINGS.MAX_MESSAGES_PER_CONVERSATION]
      ),
      10
    ),
  }
}
